import React from 'react';
import { Checkbox } from './ui/Checkbox';
import { RadioGroup, RadioGroupItem } from './ui/RadioGroup';
import { Slider } from './ui/Slider';
import { Label } from './ui/Label';
import { Separator } from './ui/Separator';
const GOALS = ['Recovery', 'Weight management', 'Performance', 'Skin & Hair', 'Longevity', 'Sleep'];
const CATEGORIES = ['Healing', 'GLP-1', 'Growth hormone', 'Cosmetic', 'Nootropic', 'Anti-aging'];
export type FilterState = {
  goals: string[];
  categories: string[];
  purity: '99' | '98' | 'all';
  price: [number, number];
  inStockOnly: boolean;
};
export const initialFilters: FilterState = {
  goals: [],
  categories: [],
  purity: 'all',
  price: [0, 200],
  inStockOnly: false
};
type Props = {
  filters: FilterState;
  onChange: (next: FilterState) => void;
};
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div className="py-6" data-id="element-172">
      <h3 className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-4" data-id="element-173">
        {title}
      </h3>
      {children}
    </div>;
}
export function Filters({
  filters,
  onChange
}: Props) {
  const toggle = (key: 'goals' | 'categories', value: string) => {
    const list = filters[key];
    const next = list.includes(value) ? list.filter(v => v !== value) : [...list, value];
    onChange({
      ...filters,
      [key]: next
    });
  };
  return <aside className="w-full" data-id="element-174">
      <div className="flex items-center justify-between pb-1" data-id="element-175">
        <h2 className="text-base font-semibold tracking-tight text-foreground" data-id="element-176">
          Filters
        </h2>
        <button onClick={() => onChange(initialFilters)} className="text-[12px] text-muted-foreground hover:text-foreground transition-colors" data-id="element-177">
          Reset
        </button>
      </div>
      <Separator data-id="element-178" />

      <Section title="Goal" data-id="element-179">
        <div className="space-y-2.5" data-id="element-180">
          {GOALS.map(g => <label key={g} className="flex items-center gap-2.5 cursor-pointer text-sm text-foreground" data-id="element-181">
              <Checkbox checked={filters.goals.includes(g)} onCheckedChange={() => toggle('goals', g)} id={`goal-${g}`} data-id="element-182" />
              <span data-id="element-183">{g}</span>
            </label>)}
        </div>
      </Section>
      <Separator data-id="element-184" />

      <Section title="Category" data-id="element-185">
        <div className="space-y-2.5" data-id="element-186">
          {CATEGORIES.map(c => <label key={c} className="flex items-center gap-2.5 cursor-pointer text-sm text-foreground" data-id="element-187">
              <Checkbox checked={filters.categories.includes(c)} onCheckedChange={() => toggle('categories', c)} id={`cat-${c}`} data-id="element-188" />
              <span data-id="element-189">{c}</span>
            </label>)}
        </div>
      </Section>
      <Separator data-id="element-190" />

      <Section title="Purity" data-id="element-191">
        <RadioGroup value={filters.purity} onValueChange={v => onChange({
        ...filters,
        purity: v as FilterState['purity']
      })} className="space-y-2.5" data-id="element-192">
          {[{
          v: '99',
          label: '≥ 99%'
        }, {
          v: '98',
          label: '≥ 98%'
        }, {
          v: 'all',
          label: 'All'
        }].map(o => <div key={o.v} className="flex items-center gap-2.5" data-id="element-193">
              <RadioGroupItem value={o.v} id={`purity-${o.v}`} data-id="element-194" />
              <Label htmlFor={`purity-${o.v}`} className="text-sm font-normal cursor-pointer" data-id="element-195">
                {o.label}
              </Label>
            </div>)}
        </RadioGroup>
      </Section>
      <Separator data-id="element-196" />

      <Section title="Price" data-id="element-197">
        <Slider value={filters.price} onValueChange={v => onChange({
        ...filters,
        price: [v[0], v[1]] as [number, number]
      })} min={0} max={200} step={5} data-id="element-198" />
        <div className="mt-4 flex items-center justify-between font-mono text-[12px] text-muted-foreground" data-id="element-199">
          <span data-id="element-200">£{filters.price[0]}</span>
          <span data-id="element-201">£{filters.price[1]}</span>
        </div>
      </Section>
      <Separator data-id="element-202" />

      <Section title="Availability" data-id="element-203">
        <label className="flex items-center gap-2.5 cursor-pointer text-sm text-foreground" data-id="element-204">
          <Checkbox checked={filters.inStockOnly} onCheckedChange={v => onChange({
          ...filters,
          inStockOnly: !!v
        })} id="in-stock" data-id="element-205" />
          <span data-id="element-206">In stock only</span>
        </label>
      </Section>
    </aside>;
}